// Which door the user came in by, and whether their account may use it.
//
// Both kinds of staff used to share /admin-login, so nothing could tell a
// hospital user from a BTD one until well after they were inside. The path
// now says which they claim to be, and the account has to back it up.

export const FACILITY = "facility";
export const BTD = "btd";

// The account that belongs to no centre and no facility, and so would fail
// either check. Recognised by address rather than by route, so it can sign
// in at whichever door it reaches - there is no admin door to find.
export const ADMIN_EMAIL = "admin@bbms.com";

// MFA redirects away from the login page, so the choice has to survive the
// round trip. sessionStorage rather than local: it belongs to this attempt,
// not to the browser.
const KEY = "bbms.loginPortal";

/**
 * The portal the current URL represents.
 *
 * /admin-login is kept working so existing bookmarks do not 404, but it is
 * no longer a door of its own: it is treated as the BTD one, and anybody
 * arriving there is checked like any other BTD login.
 */
export const portalFromPath = (pathname = window.location.pathname) =>
  String(pathname).includes("/facility-login") ? FACILITY : BTD;

export const rememberPortal = (portal) => {
  try {
    sessionStorage.setItem(KEY, portal);
  } catch (e) {
    // Private browsing, or storage disabled. The path check still works for
    // a direct login; only the MFA round trip loses the choice.
  }
};

export const recallPortal = () => {
  try {
    return sessionStorage.getItem(KEY) || null;
  } catch (e) {
    return null;
  }
};

export const forgetPortal = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    // Nothing to clean up if it was never stored.
  }
};

const hasHospitals = (user) =>
  (Array.isArray(user?.hospitals) && user.hospitals.length > 0) ||
  Boolean(user?.hospital?._id || (user?.hospital && user.hospital !== ""));

const hasCenters = (user) =>
  Array.isArray(user?.center)
    ? user.center.length > 0
    : Boolean(user?.center);

/**
 * Whether this account may use this door, and what to say if not.
 *
 * An account with both a centre and hospitals may use either — plenty of
 * people genuinely work across both, and locking one out to be tidy would
 * strand them. superAdmin is exempt; it belongs to no facility by design.
 *
 * Returns null when the login may proceed, otherwise the message to show.
 */
export const isAdminAccount = (user) =>
  String(user?.role || "").toLowerCase() === "superadmin" ||
  [user?.email, user?.username].some(
    (value) => String(value || "").trim().toLowerCase() === ADMIN_EMAIL,
  );

export const portalRefusal = (user, portal) => {
  if (!user) return null;

  // Passes at either door. It has no centre and no facility to check, and
  // sending it to a third address to sign in only hides where it lives.
  if (isAdminAccount(user)) return null;

  // Refuse only when the account positively belongs at the other door.
  //
  // An account with neither a centre nor a hospital is let through, as it
  // always was. Badge accounts are like this - a virtual badge used to
  // enter blood grouping results has no facility of its own - and refusing
  // them locked real users out of a system they had been using all along.
  // There is nowhere else to send them, so the message would be a dead end.
  if (portal === FACILITY && !hasHospitals(user) && hasCenters(user)) {
    return (
      "You aren't a facility staff. Please log in using BTD Staff or Donor."
    );
  }

  if (portal === BTD && !hasCenters(user) && hasHospitals(user)) {
    return (
      "There are no centers assigned to your account. " +
      "Please log in using Health Facility or Donor."
    );
  }

  return null;
};
